import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Receipt } from 'lucide-react';
import { Receipt as ReceiptType, ReceiptStatus } from '@/types/receipt';
import { BankAccountsStatus, TransactionSuggestion } from '@/types/payments';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { formatCurrency } from '@/utils/paymentUtils';
import { toast } from 'sonner';
import { useBankAccounts } from '@/hooks/useBankAccounts';
import ReceiptsTable from './ReceiptsTable';
import MarkAsMatchedModal from './MarkAsMatchedModal';
import ReceiptDetailDialog from "./ReceiptDetailDialog";
import { settledRefusalMessage } from '@/utils/errorUtils';

type TabValue = 'all' | 'unmatched' | 'matched';

const TAB_FILTERS: Record<TabValue, ReceiptStatus | undefined> = {
  all: undefined,
  unmatched: 'unmatched',
  matched: 'matched',
};

const ReceiptsPage: React.FC = () => {
  const t = useTranslations('consultantBooks.receipts');
  const [searchParams] = useSearchParams();
  const receiptIdFromUrl = searchParams.get('id');

  const [receipts, setReceipts] = useState<ReceiptType[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>('all');

  // Bank accounts for determining modal mode. #498 whole-branch review finding I2: must
  // distinguish "still loading" / "fetch failed or bank consent expired" from "genuinely no
  // accounts connected" — see BankAccountsStatus's docstring (src/types/payments.ts). Silently
  // treating loading/error as `false` would route a company whose accounts ARE connected through
  // the trusted, unaudited "paid externally" write with no auto_match_decisions row at all.
  const { accounts, loadingAccounts, accountsError, consentRequiredError } = useBankAccounts();
  const hasBankAccounts: BankAccountsStatus = loadingAccounts
    ? "loading"
    : accountsError || consentRequiredError
    ? "error"
    : accounts.length > 0;

  // State for matching modal
  const [selectedReceipt, setSelectedReceipt] = useState<ReceiptType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // State for detail dialog
  const [detailReceipt, setDetailReceipt] = useState<ReceiptType | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const loadReceipts = async () => {
    try {
      setLoading(true);
      const statusFilter = TAB_FILTERS[activeTab];
      const data = await ServiceRegistry.getReceiptService().fetchReceipts(statusFilter);
      setReceipts(data.slice().sort((a, b) => b.created_at.localeCompare(a.created_at)));
    } catch (error) {
      toast.error(t('toasts.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReceipts();
  }, [activeTab]);

  const handleMatchReceipt = async (receiptId: string, transaction: TransactionSuggestion) => {
    try {
      await ServiceRegistry.getReceiptService().matchReceipt(receiptId, {
        id: transaction.transactionId,
        amount: transaction.amount,
        currency: transaction.currency,
        date: transaction.date,
        // Required to resolve transactionId (a suggestion's effective id, not necessarily
        // bank_transactions.id) back to the real internal row server-side (issue #834).
        bankAccountId: transaction.accountId,
      });
      toast.success(t('toasts.matched'));
      setIsModalOpen(false);
      setSelectedReceipt(null);
      loadReceipts();
    } catch (error) {
      // ReceiptApiClient already toasted any other failure with the server's own sentence.
      const settled = settledRefusalMessage(error);
      if (settled) toast.error(settled);
    }
  };

  const handleMarkMatchedExternally = async (receiptId: string, formattedDate: string, actualSekAmount?: number) => {
    try {
      const matchedAt = new Date(formattedDate).toISOString();
      await ServiceRegistry.getReceiptService().markAsMatchedExternally(receiptId, matchedAt, actualSekAmount);
      toast.success(t('toasts.markedExternally'));
      loadReceipts();
    } catch (error) {
      toast.error(t('toasts.markFailed'));
      console.error(error);
    }
  };

  // Handle the zero-match / "None of these match" acknowledgment write (issue #498, Task 5 —
  // MarkAsMatchedModal's case c). Distinct from handleMarkMatchedExternally above: this always
  // routes through ReceiptService.acknowledgeExternalMatch, which writes an audited
  // auto_match_decisions row on the backend, rather than the trusted no-audit case-a write.
  const handleAcknowledgeExternalMatch = async (receiptId: string, formattedDate: string, actualSekAmount?: number) => {
    try {
      const matchedAt = new Date(formattedDate).toISOString();
      await ServiceRegistry.getReceiptService().acknowledgeExternalMatch(receiptId, matchedAt, actualSekAmount);
      toast.success(t('toasts.acknowledged'));
      loadReceipts();
    } catch (error) {
      toast.error(t('toasts.acknowledgeFailed'));
      console.error(error);
    }
  };

  const handleOpenMatchModal = (receipt: ReceiptType) => {
    setSelectedReceipt(receipt);
    setIsModalOpen(true);
  };

  const handleRowClick = (receipt: ReceiptType) => {
    setDetailReceipt(receipt);
    setIsDetailOpen(true);
  };

  // Deep-link support (e.g. "View Receipt" from the transactions page): open the
  // matching receipt's detail dialog once receipts have loaded. Switches to the
  // "all" tab first since the target receipt's status may not match the currently
  // active tab's filter.
  useEffect(() => {
    if (!receiptIdFromUrl || receipts.length === 0) {
      return;
    }
    const targetReceipt = receipts.find((r) => r.id === receiptIdFromUrl);
    if (targetReceipt) {
      setActiveTab('all');
      handleRowClick(targetReceipt);
    } else {
      toast.error(t('toasts.notFound'));
    }
  }, [receiptIdFromUrl, receipts]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">{t('title')}</h1>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)}>
        <TabsList className="w-full justify-start mb-4">
          <TabsTrigger value="all">{t('tabs.all')}</TabsTrigger>
          <TabsTrigger value="unmatched">{t('tabs.unmatched')}</TabsTrigger>
          <TabsTrigger value="matched">{t('tabs.matched')}</TabsTrigger>
        </TabsList>
      </Tabs>

      {receipts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Receipt className="h-12 w-12 mb-4" />
          <p>{t('empty')}</p>
        </div>
      ) : (
        <ReceiptsTable
          receipts={receipts}
          onMatch={handleOpenMatchModal}
          onRowClick={handleRowClick}
          formatCurrency={formatCurrency}
        />
      )}

      <MarkAsMatchedModal
        open={isModalOpen}
        setOpen={setIsModalOpen}
        receipt={selectedReceipt}
        hasBankAccounts={hasBankAccounts}
        onMatchTransaction={handleMatchReceipt}
        onMarkMatchedExternally={handleMarkMatchedExternally}
        onAcknowledgeExternalMatch={handleAcknowledgeExternalMatch}
        formatCurrency={formatCurrency}
      />

      <ReceiptDetailDialog
        receipt={detailReceipt}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onMatch={handleOpenMatchModal}
        formatCurrency={formatCurrency}
      />
    </div>
  );
};

export default ReceiptsPage;
